import { useEffect, useState, useCallback } from 'react'
import type { Settlement, PaginatedResult } from '@nexbit/types'
import { apiAdminGetSettlements, ApiRequestError } from '@/lib/api'
import { format } from 'date-fns'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react'
import { useT } from '@/contexts/language'

type SettlementStatus = Settlement['status']

function settlementVariant(
  status: SettlementStatus,
): 'success' | 'destructive' | 'warning' | 'default' | 'outline' {
  switch (status) {
    case 'completed':
      return 'success'
    case 'failed':
      return 'destructive'
    case 'pending':
    case 'processing':
      return 'warning'
    default:
      return 'outline'
  }
}

function netProfit(s: Settlement): number {
  return Number(s.totalBetAmount) - Number(s.totalWinAmount)
}

function SettlementsTable() {
  const t = useT()
  const [data, setData] = useState<PaginatedResult<Settlement> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)

  const fetchPage = useCallback(async (p: number) => {
    setLoading(true)
    setError(null)
    try {
      const result = await apiAdminGetSettlements(p, 20)
      setData(result)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.common.error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchPage(page)
  }, [fetchPage, page])

  if (loading) {
    return (
      <div className="flex flex-col">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="flex items-center gap-3 px-3 py-3 border-b border-border last:border-0"
          >
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-5 w-20 ml-auto" />
          </div>
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4" role="alert">
        <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
        <p className="font-mono text-xs text-destructive">{error}</p>
      </div>
    )
  }

  if (!data || data.items.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="font-mono text-sm text-muted-foreground">{t.settlements.noSettlements}</p>
      </div>
    )
  }

  const totalPages = Math.ceil(data.total / data.limit)

  return (
    <div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t.settlements.round}</TableHead>
            <TableHead className="text-right">{t.settlements.totalBets}</TableHead>
            <TableHead className="text-right">{t.settlements.totalBetAmount}</TableHead>
            <TableHead className="text-right">{t.settlements.totalWinAmount}</TableHead>
            <TableHead className="text-right">{t.reports.netProfit}</TableHead>
            <TableHead>{t.settlements.processedAt}</TableHead>
            <TableHead className="text-right">{t.settlements.status}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.items.map((s) => {
            const profit = netProfit(s)
            return (
              <TableRow key={s.id}>
                <TableCell className="font-mono text-xs">{s.roundId.slice(0, 8)}</TableCell>
                <TableCell className="font-mono text-xs text-right">{s.totalBets}</TableCell>
                <TableCell className="font-mono text-xs text-right">
                  {Number(s.totalBetAmount).toLocaleString()}
                </TableCell>
                <TableCell className="font-mono text-xs text-right">
                  {Number(s.totalWinAmount).toLocaleString()}
                </TableCell>
                <TableCell
                  className={
                    'font-mono text-xs text-right font-semibold ' +
                    (profit >= 0 ? 'text-success' : 'text-destructive')
                  }
                >
                  {profit >= 0 ? '+' : ''}{profit.toLocaleString()}
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">
                  {s.processedAt
                    ? format(new Date(s.processedAt), 'dd/MM HH:mm:ss')
                    : '—'}
                </TableCell>
                <TableCell className="text-right">
                  <Badge variant={settlementVariant(s.status)}>{s.status}</Badge>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-2 py-3 border-t border-border">
          <span className="font-mono text-xs text-muted-foreground">
            {t.common.page} {page} {t.common.of} {totalPages} ({data.total})
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              aria-label="หน้าก่อน"
              className="h-7 w-7 p-0"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              aria-label="หน้าถัดไป"
              className="h-7 w-7 p-0"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function AdminSettlementsPage() {
  const t = useT()
  return (
    <div className="flex flex-col gap-4 p-4 max-w-5xl mx-auto">
      <h1 className="text-xl font-semibold tracking-tight">{t.settlements.title}</h1>
      <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden">
        <SettlementsTable />
      </div>
    </div>
  )
}
